import React from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  ArrowLeft, Sparkle, ArrowSquareOut, CheckCircle, Clock,
  FileText, ArrowClockwise, X, ChatCircleDots, Trophy, CaretDown,
  Gavel, Warning, CheckCircle as CheckCircleIcon, Hourglass,
  EnvelopeSimple, ArrowRight, WarningCircle, ArrowsClockwise,
  MagnifyingGlass, CaretRight, User, CalendarBlank, TextAa, Hash,
  Package, CurrencyDollar, Coins, Buildings, Receipt, ClipboardText,
  PaperPlaneTilt, ShieldCheck,
} from "@phosphor-icons/react";

const FUSION_NEGOTIATIONS_URL =
  "https://erse-dev3.fa.em8.oraclecloud.com/fscmUI/faces/FndOverview" +
  "?pageParams=fndGlobalItemNodeId%3DitemNode_procurement_negotiations" +
  "&fndGlobalItemNodeId=itemNode_procurement_negotiations" +
  "&_adf.no-new-window-redirect=true&_afrWindowMode=2&_afrFS=16&_afrMT=screen";

function NegotiationStatusBadge({ status }) {
  const map = {
    published: { cls: "bg-emerald-100 text-emerald-700", label: "Published" },
    pending_approval: { cls: "bg-amber-100 text-amber-700", label: "Pending approval" },
    draft: { cls: "bg-gray-200 text-gray-600", label: "Draft" },
  };
  const s = map[status] || { cls: "bg-blue-100 text-blue-700", label: status || "Unknown" };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${s.cls}`}>
      {s.label}
    </span>
  );
}

function Countdown({ closeDate, status }) {
  const [now, setNow] = React.useState(() => Date.now());
  const isLive = status === "published" || status === "pending_approval";

  React.useEffect(() => {
    if (!closeDate || !isLive) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [closeDate, isLive]);

  if (!isLive) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
        <Clock size={11} weight="bold" /> Not published
      </span>
    );
  }

  if (!closeDate) return <span className="text-gray-300">{"\u2014"}</span>;

  let target = new Date(closeDate);
  if (isNaN(target.getTime())) {
    return <span className="text-gray-300">{"\u2014"}</span>;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(closeDate).trim())) {
    target = new Date(target.getTime() + (24 * 60 - 1) * 60 * 1000);
  }

  const diffMs = target.getTime() - now;

  if (diffMs <= 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
        <Clock size={11} weight="fill" /> Closed
      </span>
    );
  }

  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  const pad = (n) => String(n).padStart(2, "0");
  const clock = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  const label = days > 0 ? `${days}d ${clock}` : clock;

  const color =
    diffMs < 60 * 60 * 1000 ? "text-red-600"
    : diffMs < 24 * 60 * 60 * 1000 ? "text-amber-600"
    : "text-gray-700";

  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums ${color}`}>
      <Clock size={11} weight="bold" /> {label}
    </span>
  );
}
function EvaluationSection({ rowId, currency }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [openRows, setOpenRows] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: res } = await api.get(`/erp-agents/rfp/negotiations/${rowId}/evaluation`);
      setData(res);
      if (res?.error) setErr(res.error);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load evaluation.");
    } finally {
      setLoading(false);
    }
  }, [rowId]);

  React.useEffect(() => { load(); }, [load]);

  const ccy = currency || "";
  const fmt = (n) => (n == null ? "\u2014" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const dateOnly = (d) => (d ? String(d).slice(0, 10) : "\u2014");
  const toggle = (k) => setOpenRows((p) => ({ ...p, [k]: !p[k] }));

  const evals = data?.evaluations || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
          <ClipboardText size={12} weight="fill" className="text-[#0020C2]" />
          Supplier evaluation {evals.length > 0 && `(${evals.length})`}
        </div>
        <button onClick={load}
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-[#0020C2]">
          <ArrowClockwise size={11} weight="bold" /> Refresh
        </button>
      </div>

      {loading && (
        <div className="py-4 text-xs text-gray-400 flex items-center gap-2">
          <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
          Evaluating suppliers...
        </div>
      )}

      {!loading && err && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{err}</div>
      )}

      {!loading && !err && evals.length === 0 && (
        <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
          No responses to evaluate yet.
        </div>
      )}

      {!loading && !err && evals.length > 0 && (
        <div className="space-y-2.5">
          {data?.note && (
            <div className="rounded-lg border border-[#4A2C17]/20 bg-[#4A2C17]/5 px-3 py-2.5 flex items-start gap-2">
              <Warning size={15} weight="fill" className="text-[#4A2C17] mt-0.5 shrink-0" />
              <div className="text-[11px] text-[#4A2C17] leading-relaxed">{data.note}</div>
            </div>
          )}

          {evals.map((ev, i) => {
            const k = String(ev.response_number ?? ev.supplier_id ?? i);
            const isLowest = ev.supplier_name === data.lowest_quote_supplier;
            const isOpen = !!openRows[k];
            const prof = ev.profile || {};
            const po = ev.po_history || {};
            const certs = ev.certifications || [];

            return (
              <div key={k}
                className={`rounded-lg border overflow-hidden ${isLowest ? "border-[#0020C2]/40" : "border-gray-200"}`}>
                <button onClick={() => toggle(k)}
                  className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-gray-900 truncate">{ev.supplier_name}</span>
                      {isLowest && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider
                          bg-[#0020C2]/10 text-[#0020C2] px-1.5 py-0.5 rounded">
                          <Coins size={9} weight="fill" /> Lowest quote
                        </span>
                      )}
                      {prof.status && (
                        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          String(prof.status).toUpperCase() === "ACTIVE"
                            ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>
                          {prof.status}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                      {prof.business_relationship && <span>{prof.business_relationship}</span>}
                      {prof.supplier_type && <><span className="text-gray-300">{"\u00b7"}</span><span>{prof.supplier_type}</span></>}
                      <span className="text-gray-300">{"\u00b7"}</span>
                      <span>{po.past_orders || 0} past order{po.past_orders === 1 ? "" : "s"}</span>
                      {certs.length > 0 && (
                        <><span className="text-gray-300">{"\u00b7"}</span>
                          <span className="inline-flex items-center gap-0.5 text-emerald-600">
                            <ShieldCheck size={10} weight="fill" /> {certs.length} cert{certs.length > 1 ? "s" : ""}
                          </span></>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-sm text-gray-900">{ccy} {fmt(ev.quote_total)}</div>
                    {ev.lines?.length > 0 && (
                      <div className="text-[10px] text-gray-400">{ev.lines.length} line{ev.lines.length > 1 ? "s" : ""}</div>
                    )}
                  </div>
                  <CaretDown size={12} weight="bold"
                    className={`shrink-0 text-gray-400 transition-transform mt-1 ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 px-3 py-3 bg-gray-50/50 space-y-3">
                    {ev.ai_summary && (
                      <div className="border border-[#0020C2]/20 bg-[#0020C2]/5 rounded-lg p-2.5">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] mb-1 flex items-center gap-1">
                          <Sparkle size={11} weight="fill" /> AI assessment (advisory)
                        </div>
                        <p className="text-[11px] text-gray-800 leading-relaxed">{ev.ai_summary}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      <KVsmall k="Supplier since" v={dateOnly(prof.supplier_since)} />
                      <KVsmall k="Type" v={prof.supplier_type} />
                      <KVsmall k="Relationship" v={prof.business_relationship} />
                      <KVsmall k="Lifetime spend" v={po.lifetime_spend ? `${ccy} ${fmt(po.lifetime_spend)}` : "\u2014"} />
                      <KVsmall k="Past orders" v={po.past_orders ?? 0} />
                      <KVsmall k="Last order" v={dateOnly(po.last_order_date)} />
                      <KVsmall k="TRN"
                        v={prof.trn ? (<span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle size={11} weight="fill" /> {prof.trn}</span>) : "Not available"} />
                      <KVsmall k="OFAC"
                        v={prof.on_ofac_list ? (<span className="text-red-600 font-semibold">Listed</span>)
                          : (<span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={11} weight="fill" /> Clear</span>)} />
                    </div>

                    {po.categories?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Categories supplied to you</div>
                        <div className="flex flex-wrap gap-1">
                          {po.categories.slice(0, 8).map((c, ci) => (
                            <span key={ci} className="text-[10px] bg-[#4A2C17]/8 text-[#4A2C17] rounded px-1.5 py-0.5">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 flex items-center gap-1">
                        <ShieldCheck size={11} weight="bold" /> Certifications
                      </div>
                      {certs.length === 0 ? (
                        <div className="text-[11px] text-gray-400 italic">Not available</div>
                      ) : (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <table className="w-full text-[11px]">
                            <thead className="bg-gray-50 text-gray-400">
                              <tr>
                                <th className="text-left font-semibold px-2 py-1">Classification</th>
                                <th className="text-left font-semibold px-2 py-1">Agency</th>
                                <th className="text-left font-semibold px-2 py-1">Number</th>
                                <th className="text-left font-semibold px-2 py-1">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {certs.map((c, ci) => (
                                <tr key={ci} className="border-t border-gray-100">
                                  <td className="px-2 py-1 text-gray-700">{c.classification || "\u2014"}</td>
                                  <td className="px-2 py-1 text-gray-600">{c.certifying_agency || "\u2014"}</td>
                                  <td className="px-2 py-1 font-mono text-gray-600">{c.certificate_number || "\u2014"}</td>
                                  <td className="px-2 py-1">
                                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                      String(c.status).toUpperCase() === "CURRENT"
                                        ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>
                                      {c.status || "\u2014"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {ev.lines?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Quoted lines</div>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <table className="w-full text-[11px]">
                            <thead className="bg-gray-50 text-gray-400">
                              <tr>
                                <th className="text-left font-semibold px-2 py-1">Line</th>
                                <th className="text-right font-semibold px-2 py-1">Qty</th>
                                <th className="text-right font-semibold px-2 py-1">Price</th>
                                <th className="text-right font-semibold px-2 py-1">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ev.lines.map((l, li) => (
                                <tr key={li} className="border-t border-gray-100">
                                  <td className="px-2 py-1 text-gray-700">{l.description || `Line ${l.line_number ?? li + 1}`}</td>
                                  <td className="px-2 py-1 text-right font-mono text-gray-700">{l.quantity ?? "\u2014"}</td>
                                  <td className="px-2 py-1 text-right font-mono text-gray-700">{fmt(l.unit_price)}</td>
                                  <td className="px-2 py-1 text-right font-mono font-semibold text-gray-800">{fmt(l.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => window.open(FUSION_NEGOTIATIONS_URL, "_blank", "noopener,noreferrer")}
              className="inline-flex items-center gap-1.5 border border-[#0020C2]/30 text-[#0020C2] px-4 py-2
                rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#0020C2]/5">
              <ArrowSquareOut size={13} weight="bold" /> Complete award in Oracle
            </button>
            <span className="text-[10px] text-gray-400">The formal award is finalized in Oracle Fusion.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function KVsmall({ k, v }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">{k}</span>
      <span className="text-[11px] font-medium text-gray-900 break-words">{v ?? "\u2014"}</span>
    </div>
  );
}
function ResponsesSection({ rowId, currency }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [openRows, setOpenRows] = React.useState({});
  const [lastUpdated, setLastUpdated] = React.useState(null);
  const [nowTick, setNowTick] = React.useState(() => Date.now());

  const load = React.useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setErr("");
    try {
      const { data: res } = await api.get(`/erp-agents/rfp/negotiations/${rowId}/responses`);
      setData(res);
      setLastUpdated(Date.now());
      if (res?.error) setErr(res.error);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load responses.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [rowId]);

  React.useEffect(() => { load(false); }, [load]);

  React.useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  React.useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 10000);
    return () => clearInterval(t);
  }, []);

  const agoLabel = React.useMemo(() => {
    if (!lastUpdated) return "";
    const s = Math.max(0, Math.floor((nowTick - lastUpdated) / 1000));
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  }, [lastUpdated, nowTick]);

  const responses = data?.responses || [];
  const ccy = (r) => r.currency || currency || "";
  const fmt = (n) => (n == null ? "\u2014" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  const lowest = responses.reduce(
    (min, r) => (r.amount != null && (min == null || r.amount < min) ? r.amount : min),
    null
  );

  const toggle = (k) => setOpenRows((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
          <ChatCircleDots size={12} weight="fill" className="text-[#0020C2]" />
          Supplier responses {responses.length > 0 && `(${responses.length})`}
        </div>
        <div className="flex items-center gap-2">
          {agoLabel && (
            <span className="text-[9px] text-gray-400">Updated {agoLabel}</span>
          )}
          <button onClick={() => load(false)}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-[#0020C2]">
            <ArrowClockwise size={11} weight="bold" /> Refresh</button></div></div>

      {loading && (
        <div className="py-4 text-xs text-gray-400 flex items-center gap-2">
          <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
          Loading responses from Oracle...
        </div>
      )}

      {!loading && err && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {!loading && !err && responses.length === 0 && (
        <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
          No supplier responses yet.
        </div>
      )}

      {!loading && !err && responses.length > 0 && (
        <div className="space-y-2">
          {responses.map((r, i) => {
            const k = String(r.response_number ?? i);
            const isBest = r.amount != null && r.amount === lowest;
            const isOpen = !!openRows[k];
            return (
              <div key={k}
                className={`rounded-lg border overflow-hidden ${isBest ? "border-emerald-300" : "border-gray-200"}`}>
                <button onClick={() => toggle(k)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-900 truncate">{r.supplier}</span>
                      {isBest && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider
                          bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                          <Trophy size={9} weight="fill" /> Lowest
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      Response #{r.response_number}
                      {r.status && <> {"\u00b7"} {r.status}</>}
                      {r.response_date && <> {"\u00b7"} {String(r.response_date).slice(0, 10)}</>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-sm text-gray-900">
                      {ccy(r)} {fmt(r.amount)}
                    </div>
                    {r.lines?.length > 0 && (
                      <div className="text-[10px] text-gray-400">{r.lines.length} line{r.lines.length > 1 ? "s" : ""}</div>
                    )}
                  </div>
                  {r.lines?.length > 0 && (
                    <CaretDown size={12} weight="bold"
                      className={`shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  )}
                </button>

                {isOpen && r.lines?.length > 0 && (
                  <div className="border-t border-gray-100 px-3 py-2 bg-gray-50/50">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-gray-400">
                          <th className="text-left font-semibold py-1">Line</th>
                          <th className="text-right font-semibold py-1">Qty</th>
                          <th className="text-right font-semibold py-1">Price</th>
                          <th className="text-right font-semibold py-1">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.lines.map((l, li) => (
                          <tr key={li} className="border-t border-gray-100">
                            <td className="py-1 pr-2 text-gray-700">{l.description || `Line ${l.line_number ?? li + 1}`}</td>
                            <td className="py-1 text-right font-mono text-gray-700">{l.response_quantity ?? "\u2014"}</td>
                            <td className="py-1 text-right font-mono text-gray-700">{fmt(l.response_price)}</td>
                            <td className="py-1 text-right font-mono font-semibold text-gray-800">{fmt(l.response_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {r.note_to_buyer && (
                      <div className="mt-1.5 text-[10px] text-gray-500 italic">Note: {r.note_to_buyer}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NegotiationDetail({ rowId, onBack, onStatusChange }) {
  const [neg, setNeg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [publishing, setPublishing] = React.useState(false);
  const [publishMsg, setPublishMsg] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/erp-agents/rfp/negotiations/${rowId}`);
      setNeg(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load negotiation.");
    } finally {
      setLoading(false);
    }
  }, [rowId]);

  React.useEffect(() => { load(); }, [load]);

  const asText = (v, fallback) => {
    if (v == null) return fallback;
    if (typeof v === "string") return v;
    if (typeof v === "object") {
      return v.Message || v.message || v.detail || v.error || v.result || JSON.stringify(v);
    }
    return String(v);
  };

  const publish = async () => {
    setPublishing(true);
    setPublishMsg(null);
    try {
      const { data } = await api.post(`/erp-agents/rfp/negotiations/${rowId}/publish`);
      setPublishMsg({ ok: !!data?.ok, text: asText(data?.message, data?.ok ? "Published." : "Publish failed.") });
      if (data?.ok) {
        setNeg((prev) => (prev ? { ...prev, status: data.status } : prev));
        onStatusChange?.(rowId, data.status);
      }
    } catch (e) {
      setPublishMsg({ ok: false, text: asText(e?.response?.data?.detail, e.message || "Publish failed.") });
    } finally {
      setPublishing(false);
    }
  };

  const verify = () => window.open(FUSION_NEGOTIATIONS_URL, "_blank", "noopener,noreferrer");

  const suppliers = Array.isArray(neg?.suppliers) ? neg.suppliers : [];
  const lines = Array.isArray(neg?.lines) ? neg.lines : [];

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={onBack}
        className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-[#0020C2] mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to all RFPs
      </button>

      {loading && (
        <div className="py-12 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
          <span className="w-4 h-4 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
          Loading...
        </div>
      )}

      {err && <div className="py-4 text-sm text-red-600">{err}</div>}

      {!loading && !err && neg && (
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-[#0020C2]">{neg.number || "\u2014"}</span>
                <NegotiationStatusBadge status={neg.status} />
              </div>
              <h2 className="text-xl font-black tracking-tight mt-1">{neg.title}</h2>
              <div className="text-xs text-gray-500 mt-1">
                {neg.procurement_bu} {neg.buyer ? `\u00b7 Buyer: ${neg.buyer}` : ""}
              </div>
              <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                Oracle id: {neg.negotiation_id || "\u2014"}
              </div>
              {neg.close_date && (
                <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
                  <span>Closes {new Date(neg.close_date).toLocaleDateString([], {
                    month: "short", day: "numeric", year: "numeric",
                  })}</span>
                  <Countdown closeDate={neg.close_date} status={neg.status} />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button
                onClick={publish}
                disabled={publishing}
                className="inline-flex items-center gap-1.5 bg-[#0020C2] text-white px-4 py-2 rounded-lg
                  text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
                {publishing
                  ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <CheckCircle size={14} weight="bold" />}
                {publishing ? "Publishing..." : "Publish"}
              </button>
              <button
                onClick={verify}
                className="inline-flex items-center gap-1.5 border border-[#0020C2]/30 text-[#0020C2] px-4 py-2
                  rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#0020C2]/5">
                <ArrowSquareOut size={14} weight="bold" /> Verify in Fusion
              </button>
            </div>
          </div>

          {publishMsg && (
            <div className={`text-xs rounded-lg px-3 py-2 border ${
              publishMsg.ok ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                            : "bg-red-50 border-red-100 text-red-700"}`}>
              {publishMsg.text}
            </div>
          )}

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Lines</div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-gray-500">Description</th>
                    <th className="text-left px-3 py-2 font-bold text-gray-500">Category</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500">Qty</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-3 text-gray-400">No line data.</td></tr>
                  )}
                  {lines.map((l, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-2">{l.LineDescription || l.Item || l.item || "\u2014"}</td>
                      <td className="px-3 py-2">{l.CategoryName || l.category || "\u2014"}</td>
                      <td className="px-3 py-2 text-right font-mono">{l.Quantity ?? l.quantity ?? "\u2014"}</td>
                      <td className="px-3 py-2 text-right font-mono">{l.CurrentPrice ?? l.unit_price ?? "\u2014"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
              Invited suppliers
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suppliers.length === 0 && <span className="text-xs text-gray-400">No suppliers.</span>}
              {suppliers.map((s, i) => (
                <span key={i} className="text-[11px] bg-[#0020C2]/10 text-[#0020C2] rounded px-2 py-0.5">
                  {s.Supplier || s.supplier_name || s.name}
                </span>
              ))}
            </div>
          </div>

          {(neg.status === "published" || neg.status === "pending_approval") && (
            <ResponsesSection rowId={neg.id} currency={neg.currency} />
          )}

          {(neg.status === "published" || neg.status === "pending_approval") && (
            <EvaluationSection rowId={neg.id} currency={neg.currency} />
          )}
        </div>
      )}
    </div>
  );
}

function NegotiationsPane() {
  const [negotiations, setNegotiations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [selectedId, setSelectedId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/erp-agents/rfp/negotiations");
      setNegotiations(data?.negotiations || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load RFPs.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const onStatusChange = (rowId, status) => {
    setNegotiations((prev) => prev.map((n) => (n.id === rowId ? { ...n, status } : n)));
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
          <FileText size={13} weight="fill" /> RFP Negotiations
        </div>
        <button onClick={load}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-[#0020C2]">
          <ArrowClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {selectedId != null ? (
        <NegotiationDetail
          rowId={selectedId}
          onBack={() => setSelectedId(null)}
          onStatusChange={onStatusChange}
        />
      ) : (
        <>
          {loading && (
            <div className="py-12 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
              Loading RFPs...
            </div>
          )}
          {err && <div className="py-4 text-sm text-red-600">{err}</div>}
          {!loading && !err && negotiations.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-400">
              No RFPs created yet. Create one from the Chat tab.
            </div>
          )}
          {!loading && !err && negotiations.length > 0 && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Number</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Title</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Business Unit</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Closes</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {negotiations.map((n) => (
                    <tr key={n.id}
                      onClick={() => setSelectedId(n.id)}
                      className="border-t border-gray-100 hover:bg-[#0020C2]/5 cursor-pointer">
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-[#0020C2]">{n.number || "\u2014"}</span>
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate">{n.title}</td>
                      <td className="px-4 py-3 text-gray-600">{n.procurement_bu}</td>
                      <td className="px-4 py-3"><NegotiationStatusBadge status={n.status} /></td>
                      <td className="px-4 py-3"><Countdown closeDate={n.close_date} status={n.status} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {n.created_at ? new Date(n.created_at).toLocaleString([], {
                          month: "short", day: "numeric", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        }) : "\u2014"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
const money = (n, c) =>
  n == null || n === ""
    ? "\u2014"
    : `${c ? c + " " : ""}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return "\u2014";
  const dt = new Date(d);
  return isNaN(dt) ? String(d) : dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
};
const fmtDateTime = (d) => {
  if (!d) return "\u2014";
  const dt = new Date(d);
  return isNaN(dt) ? String(d)
    : dt.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const QUOTE_STATUS_STYLE = {
  READY_TO_CREATE:    { bg: "bg-blue-50",    bd: "border-blue-200",    tx: "text-[#0020C2]",   label: "Ready" },
  RESPONSE_CREATED:   { bg: "bg-emerald-50", bd: "border-emerald-200", tx: "text-emerald-700", label: "Draft created" },
  RESPONSE_SUBMITTED: { bg: "bg-emerald-100", bd: "border-emerald-300", tx: "text-emerald-800", label: "Submitted" },
  ALREADY_RESPONDED:  { bg: "bg-gray-50",    bd: "border-gray-200",    tx: "text-gray-600",    label: "Already responded" },
  NEEDS_REVIEW:       { bg: "bg-amber-50",   bd: "border-amber-200",   tx: "text-amber-700",   label: "Needs review" },
  NO_MATCH:           { bg: "bg-amber-50",   bd: "border-amber-200",   tx: "text-amber-700",   label: "No match" },
  CREATE_FAILED:      { bg: "bg-red-50",     bd: "border-red-200",     tx: "text-[#DC2626]",   label: "Failed" },
};
const quoteStyleFor = (s) =>
  QUOTE_STATUS_STYLE[(s || "").toUpperCase()] || { bg: "bg-gray-50", bd: "border-gray-200", tx: "text-gray-600", label: s || "Unknown" };

function QuoteStatusBadge({ status }) {
  const s = quoteStyleFor(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${s.bg} ${s.bd} ${s.tx}`}>
      {s.label}
    </span>
  );
}

function MatchChip({ matchedOn, band }) {
  const m = (matchedOn || "").toLowerCase();
  let label = "\u2014", cls = "border-gray-200 text-gray-500 bg-gray-50", Icon = User;
  if (m === "single_supplier") { label = "Single supplier"; cls = "border-sky-200 text-sky-700 bg-sky-50"; Icon = User; }
  else if (m === "domain")     { label = "Email domain";    cls = "border-indigo-200 text-indigo-700 bg-indigo-50"; Icon = EnvelopeSimple; }
  else if (m === "contact_name") { label = "Contact name";  cls = "border-indigo-200 text-indigo-700 bg-indigo-50"; Icon = User; }
  else if (m === "body_signature") { label = "Signature";   cls = "border-indigo-200 text-indigo-700 bg-indigo-50"; Icon = TextAa; }
  else if (m === "none")       { label = "Unmatched";       cls = "border-amber-200 text-amber-700 bg-amber-50"; Icon = WarningCircle; }
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${cls}`}>
      <Icon size={10} weight="bold" /> {label}{band ? ` \u00b7 ${band}` : ""}
    </span>
  );
}

const canProcess = (r) => (r.status || "").toUpperCase() === "READY_TO_CREATE" && !r.response_number;
const isCreated = (r) => (r.status || "").toUpperCase() === "RESPONSE_CREATED" || (!!r.response_number && (r.status || "").toUpperCase() !== "RESPONSE_SUBMITTED");
const isSubmitted = (r) => (r.status || "").toUpperCase() === "RESPONSE_SUBMITTED";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">{label}</span>
        <Icon size={16} weight="bold" className={accent || "text-[#0020C2]"} />
      </div>
      <div className="text-3xl font-black tracking-tight text-gray-900">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

const SectionTitle = ({ children, right }) => (
  <div className="flex items-center justify-between mb-2">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">{children}</div>
    {right}
  </div>
);
function Section({ title, right, children }) {
  return (
    <div>
      <SectionTitle right={right}>{title}</SectionTitle>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </div>
  );
}
function KV({ k, v, mono, strong }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">{k}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} ${strong ? "font-black text-emerald-700" : "font-medium text-gray-900"} break-words`}>
        {v ?? "\u2014"}
      </span>
    </div>
  );
}
function Flag({ ok, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {ok ? <CheckCircle size={14} weight="fill" className="text-emerald-500" />
          : <WarningCircle size={14} weight="fill" className="text-amber-500" />}
      <span className={ok ? "text-gray-700" : "text-amber-700"}>{label}</span>
    </div>
  );
}

function QuotedLines({ lines, currency }) {
  const list = Array.isArray(lines) ? lines : [];
  if (list.length === 0) {
    return (
      <div className="border border-dashed border-gray-200 bg-gray-50/60 px-3 py-4 text-center text-[11px] text-gray-400">
        No quoted lines were parsed from this email.
      </div>
    );
  }
  const total = list.reduce((s, l) => s + (Number(l.price) || 0), 0);
  return (
    <div>
      <SectionTitle right={<span className="text-[10px] text-gray-400 font-mono">{money(total, currency)}</span>}>
        Quoted lines \u00b7 {list.length}
      </SectionTitle>
      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-bold">#</th>
              <th className="px-2 py-1.5 font-bold">Description</th>
              <th className="px-2 py-1.5 font-bold">Category</th>
              <th className="px-2 py-1.5 font-bold text-right">Price</th>
              <th className="px-2 py-1.5 font-bold">Currency</th>
            </tr>
          </thead>
          <tbody>
            {list.map((l, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-2 py-1.5">{l.line_number ?? i + 1}</td>
                <td className="px-2 py-1.5 max-w-[220px]">{l.description || "\u2014"}</td>
                <td className="px-2 py-1.5">{l.category || "\u2014"}</td>
                <td className="px-2 py-1.5 text-right font-medium">{money(l.price)}</td>
                <td className="px-2 py-1.5 font-mono">{l.currency || currency || "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResponseResult({ row }) {
  const submitted = isSubmitted(row);
  return (
    <div className="border border-emerald-200 bg-emerald-50/60 p-4" data-testid="response-result">
      <div className="flex items-center gap-1.5 mb-3">
        <CheckCircle size={13} weight="fill" className="text-emerald-600" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-emerald-700">Oracle response</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        <KV k="Response number" v={row.response_number} mono strong />
        <KV k="Status" v={row.response_status || (submitted ? "Active" : "Draft")} />
        <KV k="Method" v={row.method || "EMAIL"} />
        <KV k="Surrogate" v={row.surrogate ? "Yes" : "No"} />
      </div>
      <p className="mt-3 text-[11px] text-emerald-800/80 leading-relaxed">
        {submitted
          ? "Response created and submitted in Oracle \u2014 it is now an Active bid in the negotiation."
          : "Draft created in Oracle."}
      </p>
    </div>
  );
}

function QuoteDetailDrawer({ row, onClose, onProcess, busy }) {
  if (!row) return null;
  const created = isCreated(row) || isSubmitted(row);
  const actionable = canProcess(row);
  const lines = Array.isArray(row.lines) ? row.lines : [];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl"
        data-testid="rfp-detail-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 pt-4 pb-4 z-10">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-1">
                RFP response \u00b7 row #{row.id}
              </div>
              <h3 className="text-xl font-black tracking-tight break-words">{row.negotiation || "\u2014"}</h3>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <QuoteStatusBadge status={row.status} />
                <MatchChip matchedOn={row.matched_on} band={row.match_band} />
              </div>
            </div>
            <button onClick={onClose} data-testid="drawer-close" className="shrink-0 ml-3"><X size={18} /></button>
          </div>

          {(row.email_from || row.email_subject || row.sender_email) && (
            <div className="mt-3 border border-gray-200 bg-gray-50/70 px-3 py-2.5" data-testid="drawer-email-meta">
              <div className="flex items-center gap-1.5 mb-2">
                <EnvelopeSimple size={12} weight="bold" className="text-[#4A2C17]" />
                <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-gray-500">From email</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <User size={13} weight="bold" className="text-gray-400 mt-0.5 shrink-0" />
                  <span className="text-xs font-medium text-gray-900 truncate">{row.email_from || row.sender_email || "\u2014"}</span>
                </div>
                <div className="flex items-start gap-2">
                  <TextAa size={13} weight="bold" className="text-gray-400 mt-0.5 shrink-0" />
                  <span className="text-xs text-gray-700 leading-snug">{row.email_subject || "\u2014"}</span>
                </div>
                <div className="flex items-start gap-2">
                  <CalendarBlank size={13} weight="bold" className="text-gray-400 mt-0.5 shrink-0" />
                  <span className="text-xs text-gray-500 font-mono">{fmtDateTime(row.created_at)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">
          <Section title="Negotiation">
            <KV k="Number" v={row.negotiation} mono />
            <KV k="Title" v={row.negotiation_title} />
            <KV k="Auction header ID" v={row.auction_header_id} mono />
            <KV k="Currency" v={row.currency} />
          </Section>

          <Section title="Supplier">
            <KV k="Name" v={row.supplier} />
            <KV k="Supplier ID" v={row.supplier_id} mono />
            <KV k="Contact ID" v={row.supplier_contact_id} mono />
            <KV k="Site ID" v={row.supplier_site_id} mono />
          </Section>

          <Section title="Agent decision">
            <Flag ok={!!row.negotiation} label="Negotiation resolved" />
            <Flag ok={!!row.supplier_id} label="Supplier identified" />
            <Flag ok={!row.already_responded} label="No prior response" />
            <Flag ok={row.should_create_surrogate} label="Eligible to create" />
          </Section>

          {row.already_responded && row.existing_response_number && (
            <div className="border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs text-gray-600 flex items-center gap-2">
              <Receipt size={14} weight="bold" className="text-gray-400" />
              This supplier already has response <span className="font-mono font-bold">{row.existing_response_number}</span> \u2014 a new draft won't be created.
            </div>
          )}

          {row.ai_reason && (
            <div className="border border-blue-200 bg-blue-50 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] mb-1 flex items-center gap-1">
                <Sparkle size={11} weight="fill" /> Vaku AI reasoning
              </div>
              <p className="text-xs text-gray-800 leading-relaxed">{row.ai_reason}</p>
            </div>
          )}

          <QuotedLines lines={lines} currency={row.currency} />

          {created && <ResponseResult row={row} />}
        </div>

        {actionable && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
            <button
              onClick={() => onProcess(row)}
              disabled={busy}
              data-testid="drawer-process"
              className="w-full bg-[#0020C2] text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <PaperPlaneTilt size={13} weight="fill" /> {busy ? "Creating & submitting\u2026" : "Create & submit to Oracle"}
            </button>
            <p className="text-[10px] text-gray-400 text-center leading-snug mt-2">
              Creates the surrogate draft and submits it as a bid in one step.
            </p>
          </div>
        )}
        {isSubmitted(row) && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
            <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-800">
              <CheckCircle size={14} weight="fill" /> Submitted \u2014 response {row.response_number}
            </div>
          </div>
        )}
        {isCreated(row) && !isSubmitted(row) && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
            <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700">
              <CheckCircle size={14} weight="fill" /> Draft created \u2014 response {row.response_number}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function QuoteRow({ row, onOpen, onProcess, busy }) {
  const actionable = canProcess(row);
  const firstLine = Array.isArray(row.lines) && row.lines[0] ? row.lines[0] : null;
  return (
    <div className="w-full grid grid-cols-[1fr_auto] items-center gap-4 border border-gray-200 bg-white px-4 py-3 hover:border-[#0020C2] hover:bg-blue-50/30 transition-colors">
      <button onClick={() => onOpen(row)} data-testid={`rfp-row-${row.id}`} className="min-w-0 text-left">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm text-gray-900 truncate">{row.supplier || "Unknown supplier"}</span>
          <QuoteStatusBadge status={row.status} />
          <MatchChip matchedOn={row.matched_on} band={row.match_band} />
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono">{row.negotiation || "\u2014"}</span>
          {row.negotiation_title && <><span className="text-gray-300">\u00b7</span><span className="truncate max-w-[220px]">{row.negotiation_title}</span></>}
          {firstLine && <><span className="text-gray-300">\u00b7</span><span>{firstLine.description} {money(firstLine.price, firstLine.currency || row.currency)}</span></>}
        </div>
      </button>
      <div className="flex items-center gap-3 shrink-0">
        {isSubmitted(row) ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 border border-emerald-300 bg-emerald-50 px-2 py-0.5">
            <CheckCircle size={11} weight="fill" /> #{row.response_number}
          </span>
        ) : isCreated(row) ? (
          <span className="font-mono text-xs text-emerald-700 font-bold">#{row.response_number}</span>
        ) : actionable ? (
          <button
            onClick={() => onProcess(row)}
            disabled={busy}
            data-testid={`process-${row.id}`}
            className="inline-flex items-center gap-1.5 bg-[#0020C2] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50"
          >
            <PaperPlaneTilt size={12} weight="fill" /> Create & submit
          </button>
        ) : (
          <CaretRight size={14} weight="bold" className="text-gray-300" />
        )}
      </div>
    </div>
  );
}

function QuoteGroup({ icon: Icon, title, count, accent, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} weight="fill" className={accent ? "text-[#0020C2]" : "text-gray-400"} />
        <h2 className="text-[11px] tracking-[0.2em] uppercase font-bold text-gray-700">{title}</h2>
        <span className="text-[10px] font-bold text-gray-400">\u00b7 {count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
const QuoteEmpty = ({ children }) => (
  <div className="border border-dashed border-gray-200 bg-gray-50/50 px-4 py-6 text-center text-xs text-gray-400">{children}</div>
);

function EmailQuotesPane() {
  const [rows, setRows]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr]         = React.useState("");
  const [q, setQ]             = React.useState("");
  const [tab, setTab]         = React.useState("ready");
  const [selected, setSelected] = React.useState(null);
  const [busy, setBusy]       = React.useState(false);
  const [toast, setToast]     = React.useState(null);
  const toastTimer = React.useRef(null);

  const showToast = React.useCallback((msg) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  }, []);
  React.useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const load = React.useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/api/internal/automation/rfp/responses");
      const list = Array.isArray(data?.rows) ? data.rows : [];
      setRows(list);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load RFP responses.");
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const processResponse = async (row) => {
    if (!canProcess(row)) return;
    setBusy(true);
    try {
      const { data: created } = await api.post("/api/internal/automation/rfp/create-response", { row_id: row.id });
      if (!created?.ok) {
        showToast(created?.error || "Could not create the response.");
        await load();
        return;
      }
      const responseNumber = created.response_number;
      const createdPatch = {
        response_number: responseNumber,
        response_status: "Draft",
        status: "RESPONSE_CREATED",
      };
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...createdPatch } : r)));
      setSelected((s) => (s && s.id === row.id ? { ...s, ...createdPatch } : s));

      const { data: submitted } = await api.post("/api/internal/automation/rfp/submit-response", { row_id: row.id });
      if (submitted?.ok) {
        const submittedPatch = { response_status: "Active", status: "RESPONSE_SUBMITTED" };
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...submittedPatch } : r)));
        setSelected((s) => (s && s.id === row.id ? { ...s, ...submittedPatch } : s));
        showToast(`Response ${responseNumber} created & submitted`);
      } else {
        showToast(`Draft ${responseNumber} created, but submit failed: ${submitted?.error || "unknown error"}`);
        await load();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? JSON.stringify(detail) : (detail || e.message);
      alert("Oracle rejected the request:\n\n" + msg);
    } finally { setBusy(false); }
  };

  const norm = (s) => (s || "").toUpperCase();
  const matchText = (r) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [r.supplier, r.negotiation, r.negotiation_title, r.sender_email].some((x) => (x || "").toLowerCase().includes(t));
  };

  const visible = rows.filter(matchText);
  const ready   = visible.filter((r) => norm(r.status) === "READY_TO_CREATE");
  const done    = visible.filter((r) => isCreated(r) || isSubmitted(r));
  const review  = visible.filter((r) => ["NEEDS_REVIEW", "NO_MATCH", "ALREADY_RESPONDED", "CREATE_FAILED"].includes(norm(r.status)));

  const tabRows = tab === "ready" ? ready : tab === "created" ? done : tab === "review" ? review : visible;

  return (
    <>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 bg-[#4A2C17] text-white px-4 py-2.5 shadow-2xl" role="status">
          <CheckCircle size={14} weight="fill" className="shrink-0" />
          <span className="text-xs font-medium">{toast}</span>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
          <EnvelopeSimple size={13} weight="fill" /> Supplier Quotes
        </div>
        <button
          onClick={load}
          className="border border-[#0020C2] text-[#0020C2] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0020C2] hover:text-white flex items-center gap-2"
        >
          <ArrowsClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={ClipboardText} label="Total quotes" value={rows.length} sub="Captured from email" />
          <Stat icon={Clock}         label="Ready"        value={ready.length}   sub="To create & submit" accent="text-[#0020C2]" />
          <Stat icon={CheckCircle}   label="Submitted"    value={done.length}    sub="In Oracle" accent="text-emerald-500" />
          <Stat icon={WarningCircle} label="Review"       value={review.length}  sub="Need attention" accent="text-amber-500" />
        </div>

        <div className="flex items-center gap-1 border-b border-gray-200">
          {[
            { k: "ready",   l: `Ready \u00b7 ${ready.length}` },
            { k: "created", l: `Done \u00b7 ${done.length}` },
            { k: "review",  l: `Review \u00b7 ${review.length}` },
            { k: "all",     l: `All \u00b7 ${visible.length}` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 border border-gray-300 bg-white px-3 py-2 max-w-md">
          <MagnifyingGlass size={15} weight="bold" className="text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search supplier, negotiation, or sender\u2026"
            data-testid="rfp-search"
            className="flex-1 outline-none text-sm bg-transparent"
          />
          {q && <button onClick={() => setQ("")}><X size={13} weight="bold" className="text-gray-400" /></button>}
        </div>

        {err && (
          <div className="border border-[#DC2626] bg-red-50 px-4 py-3 text-xs text-[#DC2626] flex items-center gap-2">
            <WarningCircle size={14} weight="fill" /> {err}
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-xs tracking-[0.2em] uppercase font-bold text-gray-500">Loading\u2026</div>
        ) : (
          <QuoteGroup
            icon={tab === "created" ? CheckCircle : tab === "review" ? WarningCircle : ShieldCheck}
            title={tab === "ready" ? "Ready to create & submit" : tab === "created" ? "Created / submitted in Oracle" : tab === "review" ? "Needs review" : "All quotes"}
            count={tabRows.length}
            accent={tab === "ready"}
          >
            {tabRows.length === 0
              ? <QuoteEmpty>{tab === "ready" ? "No quotes waiting to be processed." : tab === "created" ? "Created and submitted responses appear here." : "Nothing here yet."}</QuoteEmpty>
              : tabRows.map((r) => (
                  <QuoteRow key={r.id} row={r} onOpen={setSelected} onProcess={processResponse} busy={busy} />
                ))}
          </QuoteGroup>
        )}
      </div>

      <QuoteDetailDrawer
        row={selected}
        onClose={() => setSelected(null)}
        onProcess={processResponse}
        busy={busy}
      />
    </>
  );
}

export default function RFPManager({ onClose, embedded = false, defaultTab = "negotiations" }) {
  const [activeTab, setActiveTab] = React.useState(defaultTab);

  const tabs = [
    { k: "negotiations", l: "Negotiations", icon: FileText },
    { k: "emailQuotes", l: "Email Quotes", icon: EnvelopeSimple },
  ];

  const body = (
    <>
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.k;
          return (
            <button
              key={t.k}
              onClick={() => setActiveTab(t.k)}
              data-testid={`rfp-manager-tab-${t.k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                active ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              <Icon size={14} weight={active ? "fill" : "bold"} /> {t.l}
            </button>
          );
        })}
      </div>

      {activeTab === "negotiations" ? <NegotiationsPane /> : <EmailQuotesPane />}
    </>
  );

  if (embedded) {
    return <div className="p-5">{body}</div>;
  }

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
              Oracle Fusion ERP {"\u00b7"} Vaku AI
            </div>
            <h1 className="text-2xl font-black tracking-tight mt-0.5 flex items-center gap-2">
              <FileText size={22} weight="fill" className="text-[#0020C2]" />
              Manage RFPs
            </h1>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>
          )}
        </div>
        {body}
      </div>
    </div>
  );
}